import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { Error as ErrorIcon, Info as InfoIcon } from '@material-ui/icons'
import { Autocomplete } from '@material-ui/lab'
import { KeyboardDatePicker } from '@material-ui/pickers'
import clsx from 'clsx'
import { useFormikContext } from 'formik'
import moment from 'moment'
import { CustomizedTooltip, StyledSwitch } from 'shared'
import { resetSearchParams } from 'state/modules/events'
import { OTHER_OPTION, STRING_EMPTY } from 'utils/constants'
import { getCountries, searchDisciplines } from 'utils/functions'

import { SectionBlock } from '../SectionBlock'

import { useStyles } from './InformationSection.style'

const INITIAL_DATE_FOR_CHAMPIONSHIP_CREATE = moment('01-01-2024')

const InformationSection = () => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const { values, handleChange, handleBlur, errors, touched, setFieldValue, setValues } =
    useFormikContext()

  const [disciplines, setDisciplines] = useState([])
  const [countries, setCountries] = useState([])

  useEffect(() => {
    fetchDisciplines(STRING_EMPTY)
    fetchCountries()
  }, [])

  const fetchDisciplines = async (text = STRING_EMPTY) =>
    setDisciplines(await searchDisciplines(text))

  const fetchCountries = async () => setCountries(await getCountries()(STRING_EMPTY))

  const onDisciplinesChange = (setFieldValue) => (options) =>
    setFieldValue('disciplinesSelect', options)

  const onDistanceAwardChange = (e) => {
    setValues((values) => ({
      ...values,
      distanceAward: e.target.checked,
      excludedDistances: STRING_EMPTY,
      excludedCategories: STRING_EMPTY
    }))
  }

  const onEventsCountChange = (e) => {
    e.preventDefault()

    const newEventsCount = e.target.value

    setValues((values) => ({
      ...values,
      eventsCount: newEventsCount,
      fixtures: Array.from({ length: newEventsCount }, (_, i) => {
        const existing = values.fixtures?.[i]
        return (
          existing || {
            dateNumber: i + 1,
            points: [{ position: 0, points: 0 }]
          }
        )
      })
    }))
  }

  return (
    <SectionBlock title='Información general'>
      <div className={classes.content}>
        <div className={classes.block}>
          <div className={classes.formGroup}>
            <TextField
              className={classes.input}
              name='name'
              autoComplete='off'
              fullWidth
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              helperText={errors.name || 'Máximo 100 caracteres.'}
              error={touched.name && Boolean(errors.name)}
              label='Nombre del Campeonato'
              required
              variant='outlined'
              size='small'
              margin='none'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <CustomizedTooltip
                      position='top'
                      arrow
                      title='Este cambio modificará el enlace de acceso al campeonato.'
                      buttonClassName={classes.altTooltipButton}
                      disabledClickAway
                      arrowClassName={classes.altTooltipArrow}
                      className={classes.alertTooltip}>
                      {({ handleTooltip }) => (
                        <IconButton
                          onClick={handleTooltip}
                          color='primary'
                          className={classes.alertButton}
                          size='small'>
                          <ErrorIcon />
                        </IconButton>
                      )}
                    </CustomizedTooltip>
                  </InputAdornment>
                )
              }}
            />
          </div>

          <div className={classes.disciplinesContainer}>
            <FormControl variant='outlined' className={classes.formControl}>
              <Autocomplete
                multiple
                id='disciplinesSelect-autocomplete'
                options={disciplines.filter((x) => x.label !== OTHER_OPTION.label)}
                getOptionLabel={(option) => option.label}
                value={values.disciplinesSelect}
                onChange={(event, optionSelected) =>
                  onDisciplinesChange(setFieldValue)(optionSelected)
                }
                getOptionSelected={(option) =>
                  values.disciplinesSelect.some((o) => o.value === option.value)
                }
                filterSelectedOptions
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant='outlined'
                      label={option.label}
                      key={option.value}
                      title={option.label}
                      {...getTagProps({ index })}
                      color='primary'
                      className={classes.option}
                    />
                  ))
                }
                className={classes.combo}
                clearText='Quitar todo'
                openText='Abrir desplegable'
                closeText='Cerrar desplegable'
                loadingText={<Typography align='center'> Cargando...</Typography>}
                noOptionsText={<Typography align='center'> Sin opciones</Typography>}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    className={clsx(classes.input, classes.comboInput)}
                    name='disciplines'
                    label='Seleccione Deporte/Disciplina'
                    variant='outlined'
                    error={Boolean(errors.disciplinesSelect)}
                    helperText={errors.disciplinesSelect || 'Escriba el nombre de la disciplina'}
                  />
                )}
              />
            </FormControl>
            <CustomizedTooltip
              position='right-end'
              className={classes.tooltip}
              disabledClickAway
              arrowClassName={classes.tooltipArrow}
              buttonClassName={classes.tooltipButton}
              title={
                <Typography className={classes.tooltipTitle}>
                  Si tu disciplina no figura en el listado y no es una subdisciplina de las
                  existentes, por favor envíanos un correo a:&nbsp;
                  <a
                    href='mailto:soporte@sportmetric.net'
                    target='_blank'
                    rel='noopener noreferrer'
                    className={classes.tooltipLink}>
                    soporte@sportmetric.net
                  </a>
                  &nbsp;indicándonos cuál es para que la podamos agregar.
                </Typography>
              }>
              {({ handleTooltip }) => (
                <IconButton
                  onClick={handleTooltip}
                  color='primary'
                  className={classes.infoButton}
                  size='small'>
                  <InfoIcon color='primary' />
                </IconButton>
              )}
            </CustomizedTooltip>
          </div>

          <div className={classes.dateFormGroup}>
            <Typography className={classes.date} color='primary' variant='h6'>
              Año del campeonato*
            </Typography>
            <div className={classes.pickers}>
              <div className={classes.datePickerFormGroup}>
                <KeyboardDatePicker
                  className={classes.inputDatePicker}
                  format='YYYY'
                  autoOk
                  variant='inline'
                  name='fromDate'
                  required
                  label='Desde'
                  margin='none'
                  onChange={(e) => {
                    dispatch(resetSearchParams())

                    setFieldValue('fromDate', e ? e.startOf('year') : null)
                  }}
                  id='date-picker-inline'
                  inputVariant='outlined'
                  onBlur={handleBlur}
                  minDate={INITIAL_DATE_FOR_CHAMPIONSHIP_CREATE}
                  value={values.fromDate}
                  KeyboardButtonProps={{
                    'aria-label': 'seleccione fecha'
                  }}
                  helperText={STRING_EMPTY}
                  placeholder='aaaa'
                  views={['year']}
                  error={!!errors.fromDate}
                />

                {errors.fromDate && (
                  <Typography color='error' className={classes.altErrorMessage}>
                    {errors.fromDate}
                  </Typography>
                )}
              </div>

              <div className={classes.datePickerFormGroup}>
                <KeyboardDatePicker
                  className={classes.inputDatePicker}
                  format='YYYY'
                  autoOk
                  variant='inline'
                  name='toDate'
                  required
                  label='Hasta'
                  margin='none'
                  minDate={INITIAL_DATE_FOR_CHAMPIONSHIP_CREATE}
                  onChange={(e) => {
                    dispatch(resetSearchParams())

                    setFieldValue('toDate', e ? e.endOf('year') : null)
                  }}
                  id='date-picker-inline'
                  inputVariant='outlined'
                  onBlur={handleBlur}
                  value={values.toDate}
                  views={['year']}
                  KeyboardButtonProps={{
                    'aria-label': 'seleccione fecha'
                  }}
                  placeholder='aaaa'
                  helperText={STRING_EMPTY}
                  error={!!errors.toDate || !!errors.fromDate}
                />

                {errors.toDate && (
                  <Typography color='error' className={classes.altErrorMessage}>
                    {errors.toDate}
                  </Typography>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={classes.block}>
          <div className={classes.formGroup}>
            <FormControl variant='outlined' fullWidth>
              <InputLabel
                id='country-native-simple'
                className={clsx(classes.labelSelect, errors.countryId && 'error')}>
                País*
              </InputLabel>
              <Select
                className={classes.select}
                value={values.countryId}
                required
                fullWidth
                error={Boolean(errors.countryId) && touched.countryId}
                onBlur={handleBlur}
                name='countryId'
                onChange={(e) => setFieldValue('countryId', e.target.value)}
                color='primary'
                variant='outlined'
                displayEmpty
                labelId='country-native-simple'
                label='País'>
                {countries.map((x) => (
                  <MenuItem value={x.value} key={x.value}>
                    {x.label}
                  </MenuItem>
                ))}
                <MenuItem value={STRING_EMPTY} disabled></MenuItem>
              </Select>
              <Typography
                variant='caption'
                className={classes.errorMessage}
                color={errors.countryId ? 'error' : 'inherit'}>
                {errors.countryId || 'Seleccione su País.'}
              </Typography>
            </FormControl>
          </div>
          <div className={classes.formGroup}>
            <div className={classes.otherContainer}>
              <Typography variant='h6' className={classes.numberLabel}>
                Eventos totales que conforman el Campeonato
              </Typography>
              <TextField
                name='eventsCount'
                value={values.eventsCount}
                onChange={onEventsCountChange}
                onBlur={handleBlur}
                placeholder='-'
                error={touched.eventsCount && Boolean(errors.eventsCount)}
                autoComplete='off'
                variant='outlined'
                size='small'
                InputLabelProps={{
                  shrink: true
                }}
                inputProps={{
                  step: 1,
                  min: values.events.length,
                  max: 24
                }}
                type='number'
                className={classes.input}
                margin='none'
              />
            </div>
            {errors.eventsCount && (
              <Typography
                color={touched.eventsCount ? 'error' : 'inherit'}
                className={classes.altErrorMessage}>
                {errors.eventsCount}
              </Typography>
            )}
          </div>
          <div className={classes.formGroup}>
            <div className={classes.otherContainer}>
              <Typography variant='h6' className={classes.numberLabel}>
                Eventos computables del Campeonato
              </Typography>
              <TextField
                name='computedEventsCount'
                value={values.computedEventsCount}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder='-'
                error={touched.computedEventsCount && Boolean(errors.computedEventsCount)}
                autoComplete='off'
                variant='outlined'
                size='small'
                className={clsx(classes.input, classes.numericField)}
                margin='none'
                InputLabelProps={{
                  shrink: true
                }}
                inputProps={{
                  step: 1,
                  max: values.eventsCount,
                  min: 0
                }}
                type='number'
              />
            </div>
            {errors.computedEventsCount && (
              <Typography
                color={touched.computedEventsCount ? 'error' : 'inherit'}
                className={classes.altErrorMessage}>
                {errors.computedEventsCount}
              </Typography>
            )}
          </div>

          <div className={classes.formGroup}>
            <div className={classes.otherContainer}>
              <Typography variant='h6' className={classes.numberLabel}>
                ¿Último evento obligatorio?
              </Typography>

              <div className={classes.switchContainer}>
                <Typography variant='h6' className={classes.switchTitle}>
                  No
                </Typography>
                <StyledSwitch
                  checked={values.lastEventAccreditationRequired}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name='lastEventAccreditationRequired'
                />
                <Typography variant='h6' className={classes.switchTitle}>
                  Si
                </Typography>
              </div>
            </div>
          </div>

          <div className={classes.formGroup}>
            <div className={classes.otherContainer}>
              <div className={classes.distanceAwardContainer}>
                <Typography variant='h6' className={classes.numberLabel}>
                  ¿Premiar por distancia?
                </Typography>

                <CustomizedTooltip
                  position='right-end'
                  className={classes.tooltip}
                  disabledClickAway
                  arrowClassName={classes.tooltipArrow}
                  buttonClassName={classes.tooltipButton}
                  title={
                    <Typography className={classes.tooltipTitle}>
                      Para usar esta opción Todos los eventos del campeonato deben tener las mismas
                      distancias
                    </Typography>
                  }>
                  {({ handleTooltip }) => (
                    <IconButton color='primary' size='small' onClick={handleTooltip}>
                      <InfoIcon />
                    </IconButton>
                  )}
                </CustomizedTooltip>
              </div>

              <div className={classes.switchContainer}>
                <Typography variant='h6' className={classes.switchTitle}>
                  No
                </Typography>
                <StyledSwitch
                  checked={values.distanceAward}
                  onChange={onDistanceAwardChange}
                  onBlur={handleBlur}
                  name='distanceAward'
                />
                <Typography variant='h6' className={classes.switchTitle}>
                  Si
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionBlock>
  )
}

export default InformationSection
